import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppointmentActivity, Notes } from "appointment-experience-client";
import {
  createAppointmentApi,
  fetchPatientAppointmentActivities,
} from "../../../api-helpers/AppointmentApiHelper";
import { appointmentDetailsPath } from "../../../constants/Appointment";
import { Workflow } from "../../../constants/Lookups";
import { formatDate } from "../../../helper/dateFormatter";
import { stripHtmlTags } from "../../../helper/TextUtils";
import { appointmentStatusTone } from "./appointmentStatus";
import { ActivityRow, ActivityRowItem, activityRowItem } from "./ActivityRow";

/** A row of the appointment grid, as the grid endpoint returns it. */
interface AppointmentGridRow {
  appointmentId: string;
  time: string;
  appointmentMode?: string;
  coachName?: string;
  wfStateName?: string;
}

interface AppointmentHistoryCardProps {
  patientId: string;
  /** The appointment being viewed; it is left out of its own history. */
  currentAppointmentId: string;
  /** Called once the history has loaded, whether or not every source succeeded. */
  onSettled?: () => void;
}

const HISTORY_ROW_LIMIT = 300;

const groupBy = <T,>(items: T[], key: (item: T) => string) => {
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const group = groups.get(key(item)) ?? [];
    group.push(item);
    groups.set(key(item), group);
  });
  return groups;
};

// The note typed when the appointment was booked.
const commentTag = (note: Notes): ActivityRowItem | null => {
  const text = stripHtmlTags(note.comment ?? "").trim();
  return text
    ? {
        key: note.commentId,
        tag: "NOTE",
        icon: "sticky_note_2",
        tagClass: "care-note",
        text: `"${text}"`,
        status: null,
        link: null,
      }
    : null;
};

export const AppointmentHistoryCard = ({
  patientId,
  currentAppointmentId,
  onSettled,
}: AppointmentHistoryCardProps) => {
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState<AppointmentGridRow[]>([]);
  const [activities, setActivities] = useState<AppointmentActivity[]>([]);
  const [comments, setComments] = useState<Notes[]>([]);

  useEffect(() => {
    let cancelled = false;
    const api = createAppointmentApi();
    setLoading(true);

    // Each source fails on its own, so a missing tag list never hides the appointments.
    void Promise.allSettled([
      api.appointmentControllerGetGridData({
        filters: [
          {
            propertyName: "patientId",
            operator: "equals",
            propertyValue: patientId,
          },
        ],
        sortCriteria: [{ propertyName: "time", descending: true }],
        startRow: 0,
        endRow: HISTORY_ROW_LIMIT,
      }),
      fetchPatientAppointmentActivities(patientId),
      api.appointmentControllerGetAllPatientAppointmentNotes(patientId),
    ]).then(([grid, activityRows, notes]) => {
      if (cancelled) return;
      if (grid.status === "fulfilled") {
        setAppointments((grid.value.data?.data ?? []) as AppointmentGridRow[]);
      } else {
        console.error("Error loading the appointment history:", grid.reason);
      }
      if (activityRows.status === "fulfilled") setActivities(activityRows.value);
      else console.error("Error loading appointment activities:", activityRows.reason);
      if (notes.status === "fulfilled") setComments(notes.value.data ?? []);
      else console.error("Error loading appointment notes:", notes.reason);
      setLoading(false);
      onSettled?.();
    });

    return () => {
      cancelled = true;
    };
    // onSettled is a notification only; a new callback shouldn't refetch.
  }, [patientId]);

  const { attended, noShows, canceled, pastAppointments } = useMemo(() => {
    const now = Date.now();
    const countOf = (stateName: string) =>
      appointments.filter((a) => a.wfStateName === stateName).length;
    return {
      attended: countOf(Workflow.APPOINTMENT_STATUS_COMPLETED),
      noShows: countOf(Workflow.APPOINTMENT_STATUS_NO_SHOW),
      canceled: countOf(Workflow.APPOINTMENT_STATUS_CANCELED),
      pastAppointments: appointments.filter(
        (a) =>
          a.appointmentId !== currentAppointmentId && new Date(a.time).getTime() <= now
      ),
    };
  }, [appointments, currentAppointmentId]);

  const tagsByAppointment = useMemo(() => {
    const activityGroups = groupBy(activities, (a) => a.appointmentId);
    const commentGroups = groupBy(comments, (c) => c.appointmentId);
    const tags = new Map<string, ActivityRowItem[]>();
    pastAppointments.forEach(({ appointmentId }) => {
      tags.set(appointmentId, [
        ...(activityGroups.get(appointmentId) ?? []).map(activityRowItem),
        ...(commentGroups.get(appointmentId) ?? [])
          .map(commentTag)
          .filter((tag): tag is ActivityRowItem => tag !== null),
      ]);
    });
    return tags;
  }, [activities, comments, pastAppointments]);

  const renderBody = () => {
    if (loading) {
      return (
        <span className="placeholder-wave d-block">
          <span className="placeholder col-8 mb-2"></span>
          <span className="placeholder col-6"></span>
        </span>
      );
    }
    if (pastAppointments.length === 0) {
      return <span className="appointment-card__empty">No previous appointments</span>;
    }
    return (
      <div className="appointment-history__items">
        {pastAppointments.map((appointment) => {
          const tags = tagsByAppointment.get(appointment.appointmentId) ?? [];
          const tone = appointmentStatusTone(appointment.wfStateName);
          const meta = [appointment.appointmentMode, appointment.coachName]
            .filter(Boolean)
            .join(" · ");
          return (
            <div
              className={`appointment-history__item appointment-history__item--${tone}`}
              key={appointment.appointmentId}
            >
              <div className="appointment-history__item-header">
                <span className="appointment-history__summary">
                  {/* Its click area covers the whole entry (see .appointment-history__open). */}
                  <Link
                    className="appointment-history__open"
                    to={appointmentDetailsPath(appointment.appointmentId, patientId)}
                    aria-label={`Open appointment from ${formatDate(appointment.time)}`}
                  >
                    {formatDate(appointment.time)}
                  </Link>
                  {meta && (
                    <span className="appointment-history__meta">
                      <span className="appointment-history__sep"> · </span>
                      {meta}
                    </span>
                  )}
                </span>
                {appointment.wfStateName && (
                  <span
                    className={`appointment-history__status appointment-history__status--${tone}`}
                  >
                    {appointment.wfStateName}
                  </span>
                )}
              </div>
              {tags.length > 0 && (
                <ul className="activity-list appointment-history__tags">
                  {tags.map((tag) => (
                    <ActivityRow item={tag} key={tag.key} />
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <section className="appointment-card appointment-history">
      <div className="appointment-card__header">
        <h3 className="appointment-card__title">Appointment History</h3>
        <Link
          className="a-link a-underline fs-13"
          to={`/patients/${patientId}/appointments`}
        >
          View all
        </Link>
      </div>
      {!loading && (
        <div className="appointment-history__counts">
          <span>
            <strong>{attended}</strong> attended
          </span>
          <span className="appointment-history__count--no-show">
            <strong>{noShows}</strong> no show
          </span>
          <span className="appointment-history__count--missed">
            <strong>{canceled}</strong> canceled
          </span>
        </div>
      )}
      <div className="appointment-history__list">{renderBody()}</div>
    </section>
  );
};
